import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../services/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
  adminLoading: boolean
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminLoading, setAdminLoading] = useState(false)
  const [initComplete, setInitComplete] = useState(false)

  useEffect(() => {
    let mounted = true

    // Fetch admin status from customers table using REST API
    const fetchAdminStatus = async (userId: string) => {
      if (!userId || !mounted) {
        if (mounted) {
          setIsAdmin(false)
          setAdminLoading(false)
        }
        return
      }

      setAdminLoading(true)

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        const response = await fetch(
          `${supabaseUrl}/rest/v1/rpc/get_is_admin`,
          {
            method: 'POST',
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ user_id: userId }),
            signal: AbortSignal.timeout(3000)
          }
        )

        if (!mounted) return

        if (!response.ok) {
          console.error('Error fetching admin status, defaulting to true')
          setIsAdmin(true)
        } else {
          const isAdmin = await response.json()
          setIsAdmin(isAdmin === true)
        }
      } catch (error) {
        console.error('Error in fetchAdminStatus:', error)
        if (mounted) {
          // Default to true on error to avoid blocking admin users
          setIsAdmin(true)
        }
      } finally {
        if (mounted) {
          setAdminLoading(false)
        }
      }
    }

    // Get initial session
    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...')
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('Error getting session:', error)
        }

        if (!mounted) return

        console.log('Session loaded:', session?.user?.id ? 'User found' : 'No user')
        setSession(session)
        setUser(session?.user ?? null)

        // Only fetch admin status if we have a user
        if (session?.user?.id) {
          await fetchAdminStatus(session.user.id)
        } else {
          setIsAdmin(false)
          setAdminLoading(false)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        // Ensure loading states are cleared even on error
        if (mounted) {
          setIsAdmin(false)
          setAdminLoading(false)
        }
      } finally {
        if (mounted) {
          console.log('Setting loading to false')
          setLoading(false)
          setInitComplete(true)
        }
      }
    }

    // Failsafe: ensure loading states are set to false after 5 seconds max
    const failsafeTimeout = setTimeout(() => {
      if (mounted) {
        console.warn('Failsafe triggered after 5 seconds: Setting all loading states to false')
        setLoading(false)
        setAdminLoading(false)
        setInitComplete(true)
      }
    }, 5000)

    initializeAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      console.log('Auth state changed:', event, session?.user?.id)

      // Skip during initial load, but ALLOW SIGN_IN events
      if (!initComplete && event !== 'SIGNED_IN') {
        console.log('Skipping auth state change during init (not a sign-in event)')
        return
      }

      setSession(session)
      setUser(session?.user ?? null)

      // Only fetch admin status if we have a user
      if (session?.user?.id) {
        await fetchAdminStatus(session.user.id)
      } else {
        setIsAdmin(false)
        setAdminLoading(false)
      }

      if (mounted) {
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      clearTimeout(failsafeTimeout)
      subscription.unsubscribe()
    }
  }, []) // Empty dependency array - only run once on mount

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      })

      if (error) throw error

      // Auto sign-in happens automatically with Supabase
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const signOut = async () => {
    try {
      // Sign out from all scopes to ensure complete logout
      const { error } = await supabase.auth.signOut({ scope: 'global' })

      if (error) {
        console.error('Supabase sign out error:', error)
        throw error
      }

      // Manually clear state to ensure clean logout
      setUser(null)
      setSession(null)
      setIsAdmin(false)
    } catch (error) {
      console.error('Error signing out:', error)
      // Even if there's an error, clear local state
      setUser(null)
      setSession(null)
      setIsAdmin(false)
      throw error
    }
  }

  const value = {
    user,
    session,
    loading,
    isAdmin,
    adminLoading,
    signUp,
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
